import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../api/settingsApi';
import type { CurrentPlatformRelease, PlatformRelease } from '../types';

/** Query keys scoped to the settings domain */
export const settingsKeys = {
  all: ['settings'] as const,
  features: () => [...settingsKeys.all, 'features'] as const,
  products: () => [...settingsKeys.all, 'products'] as const,
  productVersions: (productId: string) =>
    [...settingsKeys.all, 'products', productId, 'versions'] as const,
  platformReleases: () => [...settingsKeys.all, 'platform-releases'] as const,
  platformCurrent: () => [...settingsKeys.all, 'platform-current'] as const,
};

/** Fetch all feature flags */
export function useFeatureFlags() {
  return useQuery({
    queryKey: settingsKeys.features(),
    queryFn: () => settingsApi.getFeatures(),
    staleTime: 30_000,
    retry: 1,
  });
}

/** Toggle a feature flag on/off */
export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ flagId, enabled }: { flagId: string; enabled: boolean }) =>
      settingsApi.updateFeature(flagId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.features(), exact: true });
    },
  });
}

/** Fetch all products with current version + status */
export function useProducts() {
  return useQuery({
    queryKey: settingsKeys.products(),
    queryFn: () => settingsApi.getProducts(),
    staleTime: 30_000,
    retry: 1,
  });
}

/** Fetch available versions for a specific product */
export function useProductVersions(productId: string) {
  return useQuery({
    queryKey: settingsKeys.productVersions(productId),
    queryFn: () => settingsApi.getProductVersions(productId),
    enabled: !!productId,
    staleTime: 60_000,
    retry: 1,
  });
}

/** Sync the release agent, then refetch a product's available versions */
export function useRefreshProductVersions(productId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsApi.syncVersionAgent(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: settingsKeys.productVersions(productId),
        exact: true,
      });
    },
  });
}

/** Trigger a version upgrade for a product */
export function useUpgradeProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, version }: { productId: string; version: string }) =>
      settingsApi.upgradeProduct(productId, version),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.products(), exact: true });
    },
  });
}

/** Poll the endpoint at this interval. Accepts a static ms number, false to
 *  disable, or a function that is handed the query's current data so callers
 *  can adapt polling to server state without tearing the query down. */
type RefetchInterval<T> =
  | number
  | false
  | ((query: { state: { data: T | undefined } }) => number | false);

interface PlatformReleasesPollOptions {
  refetchInterval?: RefetchInterval<PlatformRelease[]>;
}

interface CurrentPlatformReleasePollOptions {
  refetchInterval?: RefetchInterval<CurrentPlatformRelease>;
}

/** Fetch platform releases */
export function usePlatformReleases(options?: PlatformReleasesPollOptions) {
  return useQuery({
    queryKey: settingsKeys.platformReleases(),
    queryFn: () => settingsApi.getPlatformReleases(),
    staleTime: 15_000,
    retry: 1,
    refetchInterval: options?.refetchInterval,
  });
}

/** Fetch the currently active (deployed) platform release */
export function useCurrentPlatformRelease(options?: CurrentPlatformReleasePollOptions) {
  return useQuery({
    queryKey: settingsKeys.platformCurrent(),
    queryFn: () => settingsApi.getCurrentPlatformRelease(),
    staleTime: 15_000,
    retry: 1,
    refetchInterval: options?.refetchInterval,
  });
}

/** Trigger platform release deploy */
export function useDeployPlatformRelease() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => settingsApi.deployPlatformRelease(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.platformReleases() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.platformCurrent() });
    },
  });
}

/** Sync the release agent, then refetch the platform release queries */
export function useRefreshPlatformReleases() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => settingsApi.syncVersionAgent(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.platformReleases() });
      queryClient.invalidateQueries({ queryKey: settingsKeys.platformCurrent() });
    },
  });
}
